import { useCallback } from "react";
import { useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSelector } from "../common/hooks/useSelector";
import { CART_ACTION_ENUM } from "../common/reducers/cart";
import CartService from "../service/CartService";
import DesignStateService from "../service/DesignStateService";
import { Currency, Size } from "../types";
import { ROUTES } from "../constant/common.constant";
import { useCurrency } from "../common/hooks/useCurrency";
import { productNameToProductLineAndFamily } from "./product.utils";
import { useTranslation } from "react-i18next";
import { trackProductAdded, trackProductRemoved } from "./analytics.utils";

/**
 * Ability to add, edit or remove cart items
 * @param {*} props
 */
export const useCart = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const tEnglish = i18n.getFixedT("en");
  const { dispatch } = useStore();
  const { getCurrency } = useCurrency();
  const userInfos = useSelector((state) => state.userInfo);

  const fetchCartItemsAndUpdateStore = useCallback(
    async (currency: Currency = getCurrency()) => {
      dispatch({
        type: CART_ACTION_ENUM.UPDATE_LOADING,
        payload: { loading: true },
      });

      const cart =
        await DesignStateService.getDesignAndPricingCartData(currency);

      dispatch({
        type: CART_ACTION_ENUM.UPDATE_CART,
        payload: { cart },
      });
    },
    [dispatch, getCurrency]
  );

  /**
   * Add to cart and redirect to cart
   */
  const addToCart = useCallback(
    async (
      designId: string,
      sizesOption: Size[],
      productName: string,
      updateCartStore: boolean = true,
      redirectToCartPage: boolean = false
    ) => {
      const payload = {
        designId,
        sizes: sizesOption.map((size) => ({
          name: size.name,
          qty: 0,
        })),
      };
      const { family, productLine } =
        productNameToProductLineAndFamily(productName);
      await CartService.addToCart(payload);

      trackProductAdded({
        cart_id: userInfos.id,
        product_id: productName, // product ID from Specs
        sku: designId, // individual design ID
        name: productName, // Hoodie, Hoodie Zip, etc. from Specs, not the name given by the user
        category: family?.labelTag ? tEnglish(family?.labelTag) : "",
        brand: tEnglish(`line.${productLine}.title`),
      });

      if (updateCartStore) {
        fetchCartItemsAndUpdateStore();
      }

      if (redirectToCartPage) {
        navigate(ROUTES.CART);
      }
    },
    [fetchCartItemsAndUpdateStore, navigate, userInfos.id, tEnglish]
  );

  /**
   * Update to cart sizes qty
   */
  const updateCartSizes = useCallback(
    async (updatedCartItem: { sizes: Size[]; cartItemId: string }) => {
      const payload = {
        id: updatedCartItem.cartItemId,
        sizes: updatedCartItem.sizes,
      };

      await CartService.updateSingleCartItemSizes(payload);

      fetchCartItemsAndUpdateStore(getCurrency());
    },
    [fetchCartItemsAndUpdateStore, getCurrency]
  );

  /**
   * Remove an item from the cart
   */
  const removeFromCart = useCallback(
    async (
      designId: string,
      currency: Currency,
      productName: string,
      updateCartStore: boolean = true
    ) => {
      await CartService.removeCartItem(designId);

      const { family, productLine } =
        productNameToProductLineAndFamily(productName);

      trackProductRemoved({
        cart_id: userInfos.id,
        product_id: productName, // product ID from Specs
        sku: designId, // individual design ID
        name: productName,
        category: family?.labelTag ? tEnglish(family?.labelTag) : "",
        brand: tEnglish(`line.${productLine}.title`),
      });

      if (updateCartStore) {
        await fetchCartItemsAndUpdateStore(currency);
      }
    },
    [fetchCartItemsAndUpdateStore, userInfos, tEnglish]
  );

  return {
    addToCart,
    updateCartSizes,
    removeFromCart,
    fetchCartItemsAndUpdateStore,
  };
};
